// 3rd party
import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// Components
import Spinner from "../../components/spinner/Spinner";
import IndiaFormDialog from "./IndiaFormDialog";
import IndiaPdf from "../indiaPdf/IndiaPdf";

// Services
import {
  AdminEntry,
  defaultAdminEntry,
  logout,
  prepareAdminEntryForEdit,
} from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";
import { syncExtraCategoriesToBlob } from "../../services/ExtraCategoriesSyncService";

// Config
import { appConfig } from "../../config/appConfig";

// AssetComponents
import visiable from "../../assets/visiable.svg";
import edit from "../../assets/edit.svg";
import delete1 from "../../assets/delete.svg";
import add from "../../assets/add.svg";
import approve from "../../assets/approve.svg";

// Styles
import "./India.scss";
import IndiaImage from "../indiaImage/IndiaImage";
import IndiaLink from "../indiaLink/IndiaLink";

syncExtraCategoriesToBlob();

interface Catgry {
  id: number;
  name: string;
}

const India = () => {
  // UseStates
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [filterList, setFilterList] = useState<AdminEntry[]>([]);
  const [filterCategory, setfilterCategory] = useState<string | null>(null);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [categories, setCategories] = useState<Catgry[]>([]);

  const [formData, setFormData] = useState<AdminEntry>(defaultAdminEntry);

  const [page, setPage] = useState(0);
  const rowsPerPage = 7;
  const [role, setRole] = useState<string>("");

  const [loading, setLoading] = useState(false);

  // UseNavigate
  const navigate = useNavigate();

  // UseEffect

  // Load role from storage

  useEffect(() => {
    const storedRole = getStorageItemLocal("role");

    if (storedRole) setRole(JSON.parse(storedRole));
  }, []);

  useEffect(() => {
    const filteredData = filterList.filter((item) => {
      const matchesCategory =
        !filterCategory ||
        (item.category || "")
          .trim()
          .toLowerCase()
          .includes(filterCategory.trim().toLowerCase());

      return matchesCategory;
    });

    setDataList(filteredData);
  }, [filterCategory]);

  useEffect(() => {
    const savedData = localStorage.getItem("adminDataList");
    if (savedData) {
      const parsedData: AdminEntry[] = JSON.parse(savedData);
      setDataList(parsedData);
      setFilterList(parsedData);

      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
      // setDataList(JSON.parse(savedData));
    }
  }, []);

  //Fetch Categories and states
  useEffect(() => {
    const fetchCategories = async () => {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      try {
        const response = await fetch(
          `${appConfig.serviceUrls.getAdminEntries}/categories`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    //call the fetch functions
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Removed rogue useEffect that was overwriting formData during edit mode
  const fetchData = async () => {
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;

      const response = await fetch(appConfig.serviceUrls.saveIndiaEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch data");

      const data: AdminEntry[] = (await response.json()) || [];
      const extraPayload = await syncExtraCategoriesToBlob();
      const extraMap = extraPayload?.ExtraIndiaEntries || {};
      const extraRows: AdminEntry[] = Object.values(extraMap).filter(
        (ex: any) =>
          ex &&
          !data.some(
            (d) =>
              Number(d.category) === Number(ex.category) ||
              (d.categoryName &&
                ex.categoryName &&
                d.categoryName.toLowerCase() === ex.categoryName.toLowerCase())
          )
      ) as AdminEntry[];
      const mergedData = [...data, ...extraRows];
      setDataList(mergedData);
      setFilterList(mergedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const resetForm = () => {
    setFormData({
      ...defaultAdminEntry,
      id: dataList.length + 1, // Ensure a new ID
      lastUpdated: new Date().toISOString(), // Update timestamp
    });
    setEditingIndex(null);
  };
  //Add New Record

  const handleAdd = () => {
    setEditingIndex(null);
    setShowForm(true);
    setFormData({
      ...defaultAdminEntry,
      id: 0, // You can also use dataList.length + 1 if needed
      lastUpdated: new Date().toISOString(),
    });
  };

  //handle publish
  const handlePublish = async (index: number) => {
    if (!window.confirm("Are you sure want to publish this entry?")) return;

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const formDataToSend = new FormData();

    const appendBasic = (key: string, value: any) => {
      if (value !== undefined && value !== null)
        formDataToSend.append(key, String(value));
    };

    // Basic fields
    appendBasic("id", selectedEntry.id);
    appendBasic("category", selectedEntry.category);
    appendBasic("subcategory", selectedEntry.subCategory);
    appendBasic("description", selectedEntry.description);
    appendBasic("description2", selectedEntry.description2);
    appendBasic("description3", selectedEntry.description3);
    appendBasic("pdfIsActive", selectedEntry.pdfIsActive);
    appendBasic("pdfName", selectedEntry.pdfName);
    appendBasic("pdfIsActive2", selectedEntry.pdfIsActive2);
    appendBasic("pdfName2", selectedEntry.pdfName2);
    appendBasic("pdfIsActive3", selectedEntry.pdfIsActive3);
    appendBasic("pdfName3", selectedEntry.pdfName3);
    appendBasic("isActive", selectedEntry.isActive);
    appendBasic("uploadBy", selectedEntry.uploadBy);
    appendBasic("approveBy", selectedEntry.approveBy);
    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("publishBy", username);
    appendBasic("status", "Published"); // override
    appendBasic("statusCode", 3);

    // Helper to fetch media URLs and convert to File objects
    const appendFilesFromUrls = async (
      key: string,
      urls: string | string[] | undefined,
    ) => {
      if (!urls) return;
      const urlArray = typeof urls === "string" ? urls.split(",") : urls;

      for (const url of urlArray) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = url.split("/").pop() || "file.dat";
          const file = new File([blob], fileName, { type: blob.type });
          formDataToSend.append(key, file);
        } catch (err) {
          console.warn(`Could not fetch ${key}:`, url, err);
        }
      }
    };

    // Media fields
    await appendFilesFromUrls("imageUrls", selectedEntry.imageUrls);
    await appendFilesFromUrls("videoUrls", selectedEntry.videoUrls);
    await appendFilesFromUrls("thumbnail", selectedEntry.thumbnail);

    await appendFilesFromUrls("pdfUrls", selectedEntry.pdfUrls);
    await appendFilesFromUrls("pdfUrls2", selectedEntry.pdfUrls2);
    await appendFilesFromUrls("pdfUrls3", selectedEntry.pdfUrls3);

    // Grid fields (loop 1–9)
    for (let i = 1; i <= 9; i++) {
      const key = `gridType${i}` as keyof AdminEntry;
      appendBasic(key, selectedEntry[key]);

      const key1 = `gridIconName${i}` as keyof AdminEntry;
      appendBasic(key1, selectedEntry[key1]);

      const key2 = `grid${i}` as keyof AdminEntry;
      appendBasic(key2, selectedEntry[key2]);

      const key3 = `gridThumbnail${i}` as keyof AdminEntry;
      appendBasic(key3, selectedEntry[key3]);
    }

    const url = `${appConfig.serviceUrls.saveIndiaEntries}/${selectedEntry.id}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");

      alert("Data published successfully");
      fetchData();
      resetForm();
      setEditingIndex(null);
    } catch (error) {
      console.error("Error publishing data:", error);
      alert("Error publishing data");
    }
  };

  // handle view
  const handleView = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const preparedEntry = prepareAdminEntryForEdit(selectedEntry);

    setFormData(preparedEntry);
    setEditingIndex(-1);
    setShowForm(true);
  };
  const handleEdit = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }

    const preparedEntry = prepareAdminEntryForEdit(selectedEntry);

    setFormData(preparedEntry);
    setEditingIndex(preparedEntry.id);
    setShowForm(true);
  };

  //Delete a record
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure want to delete this entry?")) return;
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(
        `${appConfig.serviceUrls.saveIndiaEntries}/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.status === 401) {
        logout(navigate);
        return;
      }

      if (!response.ok) throw new Error("Failed to delete");

      fetchData();
      if (editingIndex !== null && dataList[editingIndex]?.id === id) {
        setEditingIndex(null);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Component renders (Return)
  return (
    <>
      <IndiaPdf />
      <IndiaLink />
      {/* <IndiaImage /> */}
      <Box className="container-wraper">
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              paddingBottom: "5px",
            }}
          >
            <Typography className="bold-header-text">
              <b>India Level Data</b>{" "}
            </Typography>
            <Button onClick={handleAdd} size="small" className="custom-button">
              <img src={add} alt="visiable" className="img-style" />{" "}
              <Typography className="btn-text">Add</Typography>
            </Button>
          </Box>
        </>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          {/*Data Table*/}
          {loading ? (
            <>
              {" "}
              <Spinner></Spinner>
            </>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
              }}
            >
              {dataList?.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow className="custom-table-head-row">
                      <TableCell>
                        <b>Category</b>
                      </TableCell>

                      <TableCell>
                        <b>Description</b>
                      </TableCell>
                      <TableCell>
                        <b>Media</b>
                      </TableCell>

                      <TableCell className="nowrap">
                        <b>Status</b>
                      </TableCell>
                      <TableCell>
                        <b>Actions</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataList
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((item, index) => (
                        <TableRow key={index} className="custom-table-row">
                          <TableCell className="nowrap">
                            {item.categoryName || "\u00A0"}
                          </TableCell>

                          <TableCell className="common-txt nowrap">
                            <Box className="description-wraper">
                              <Typography className="description-txt">
                                {" "}
                                View Text
                              </Typography>{" "}
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  handleView(page * rowsPerPage + index);
                                }}
                              >
                                <img
                                  src={visiable}
                                  alt="visible"
                                  className="img-style"
                                />
                              </IconButton>
                            </Box>
                          </TableCell>

                          <TableCell className="common-txt nowrap">
                            <Box className="description-wraper">
                              <Typography className="icon-count common-txt">
                                {" "}
                                9{" "}
                              </Typography>
                              <Typography className="description-txt">
                                {" "}
                                Media
                              </Typography>{" "}
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  handleView(page * rowsPerPage + index);
                                }}
                              >
                                <img
                                  src={visiable}
                                  alt="visible"
                                  className="img-style"
                                />
                              </IconButton>
                            </Box>
                          </TableCell>

                          {item.status === "Approved" ? (
                            <TableCell className="approved-text">
                              {item.status}
                            </TableCell>
                          ) : item.status === "Published" ? (
                            <TableCell className="approved-text">
                              {item.status}
                            </TableCell>
                          ) : item.status === "Pending" ? (
                            <TableCell className="pending-txt">
                              {item.status}
                            </TableCell>
                          ) : (
                            <TableCell className="publish-txt">
                              {item.status}
                            </TableCell>
                          )}

                          <TableCell
                            align="left"
                            className="nowrap sticky-right-top"
                          >
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                handleEdit(page * rowsPerPage + index);
                              }}
                            >
                              <img
                                src={edit}
                                alt="visiable"
                                className="img-style"
                              />
                            </IconButton>
                            {role === "System Admin" && (
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleDelete(item.id)}
                              >
                                <img
                                  src={delete1}
                                  alt="visiable"
                                  className="img-style"
                                />
                              </IconButton>
                            )}

                            {item.status === "Approved" && (
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() =>
                                  handlePublish(page * rowsPerPage + index)
                                }
                              >
                                <img
                                  src={approve}
                                  alt="visiable"
                                  className="img-style"
                                />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  textAlign="center"
                  mt={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "1rem",
                  }}
                >
                  <Typography align="center" sx={{ mt: 2 }}>
                    No records found.
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleAdd}
                  >
                    <Add fontSize="small" />
                  </Button>
                </Box>
              )}
              {loading ? (
                <>
                  {" "}
                  <Spinner></Spinner>
                </>
              ) : (
                <></>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",

                  bottom: 0,

                  zIndex: 1,

                  overflowX: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#000",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                  }}
                >
                  Showing {page * rowsPerPage + 1} to{" "}
                  {Math.min(dataList.length, page * rowsPerPage + rowsPerPage)}{" "}
                  of {dataList.length} entries
                </Typography>
                <Pagination
                  count={Math.ceil(dataList.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(e, value) => setPage(value - 1)}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            </TableContainer>
          )}
        </Box>
        <Box>
          <IndiaFormDialog
            open={showForm}
            // onClose={handleCloseDialog}
            fetchData={fetchData}
            resetForm={resetForm}
            categories={categories}
            formData={formData}
            setFormData={setFormData}
            editingIndex={editingIndex}
            setEditingIndex={setEditingIndex}
            setShowForm={setShowForm}
            dataList={dataList}
          />
        </Box>
      </Box>
    </>
  );
};

export default India;
